import type { Address } from '@gm-commercial/location-model';

import type {
  ComponentLabels,
  Entity,
  SummaryCounts,
} from '~/common/models/common.model';

import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_EMPTY_VALUE,
} from '../../../constants/common.constant';
import type { TelemetryStatus } from '../../../models/asset.model';
import type { Fleet } from '../../../models/fleet.model';
import type { AssetCountSummary } from '../../../models/organization.model';
import type {
  BDTableData,
  SortDirection,
  TableColumnState,
  TableRowDownload,
  ToolbarButtonState,
} from '../../../models/table/table.model';
import { format } from '../../../utils/date-time.utils';

const hasDisplayAddress = (
  addr: unknown
): addr is { displayAddress: string } => {
  return (addr as { displayAddress: string }).displayAddress !== undefined;
};

/**
 * An address on one line.
 *
 * @deprecated Use `<Address>` from `@brightdrop/localization-core`, or `useFormat().address`,
 * mapping `address1` onto `line1`, `stateProvinceTerritory` onto `state` and `postalCode` onto
 * `zip`. `AddressFields` carries no `displayAddress`, so a caller relying on the one returned
 * unchanged below has to keep it itself. This is a second copy of the helper in `common.utils`,
 * identical in behaviour; both are kept so that neither set of callers has to migrate before the
 * other.
 */
export const getFullAddress = (addr: Address | BDTableData): string => {
  if (addr && typeof addr !== 'string') {
    if (hasDisplayAddress(addr)) {
      return addr.displayAddress;
    }
    const result = [
      addr.address1,
      addr.address2 || '',
      addr.city,
      addr.stateProvinceTerritory,
      addr.postalCode,
    ]
      .filter((part) => !!part && part !== ' ')
      .join(', ')
      .trim();
    return result || DEFAULT_EMPTY_VALUE;
  }
  return addr || DEFAULT_EMPTY_VALUE;
};

export const flattenSummaryCounts = <
  T extends { summaryCounts?: SummaryCounts },
>(
  data: T
): T & {
  [k in keyof SummaryCounts]: number;
} => {
  return {
    ...data,
    ...data.summaryCounts,
  };
};

export const getDefaultDownloadOptions = (
  baseFileName: string,
  selectedAssetType?: string,
  filterOptions: {
    useDisplayedColumnsOnly: boolean;
    useDisplayedRowsOnly: boolean;
  } = {
    useDisplayedColumnsOnly: true,
    useDisplayedRowsOnly: true,
  }
): {
  filename: string;
  separator: string;
  filterOptions: {
    useDisplayedColumnsOnly: boolean;
    useDisplayedRowsOnly: boolean;
  };
} => {
  return {
    filename: !selectedAssetType
      ? `${baseFileName.toLocaleUpperCase().replace(/\s/g, '_')}_${format(
          new Date(),
          'MMddyyHHmm'
        )}.csv`
      : `${baseFileName
          .concat('_')
          .concat(selectedAssetType)
          .toLocaleUpperCase()
          .replace(/\s/g, '_')}_${format(new Date(), 'MMddyyHHmm')}.csv`,
    separator: ',',
    filterOptions,
  };
};

const parseObjectData = (
  columns: TableColumnState[],
  row: TableRowDownload,
  adapters = {} as {
    [k: string]: (
      value: BDTableData,
      columnLabels?: Exclude<ComponentLabels, string>,
      dateFormat?: string
    ) => string;
  },
  labels = {} as Exclude<ComponentLabels, string>,
  dateFormat = DEFAULT_DATE_FORMAT as string
) => {
  return row.data.map((currentValue: BDTableData, colIdx: number) => {
    if (Object.hasOwn(adapters, columns[colIdx].name)) {
      const adapter = adapters[columns[colIdx].name];
      return adapter(currentValue, labels, dateFormat);
    }
    return currentValue;
  });
};

export const customOnDownload =
  (
    adapters = {} as {
      [k: string]: (
        value: BDTableData,
        columnLabels?: Exclude<ComponentLabels, string>,
        dateFormat?: string
      ) => string;
    },
    labels = {} as Exclude<ComponentLabels, string>,
    dateFormat = DEFAULT_DATE_FORMAT as string
  ) =>
  (
    buildHead: (columns: TableColumnState[]) => string,
    buildBody: (data: TableRowDownload[]) => string,
    columns: TableColumnState[],
    data: TableRowDownload[]
  ): string | boolean => {
    const json = data.reduce(
      (
        accResults: TableRowDownload[],
        row: TableRowDownload,
        rowIdx: number,
        _source: TableRowDownload[]
      ) => {
        const parsedRow = {
          index: rowIdx,
          data: parseObjectData(columns, row, adapters, labels, dateFormat),
        };
        accResults.push(parsedRow);
        return accResults;
      },
      []
    );

    // Reason for \uFEFF...mui-datatables added this link to support for UTF-8 which are for special characters: https://github.com/gregnb/mui-datatables/pull/722#issuecomment-526346440
    return '\uFEFF' + buildHead(columns) + buildBody(json);
  };

export const stringSortCompare = (
  order: SortDirection,
  first: string | undefined,
  second: string | undefined
): number => {
  if (order === 'asc') {
    if (first && second) {
      return first.localeCompare(second);
    }
    return first ? -1 : second ? 1 : 0;
  }
  if (first && second) {
    return second.localeCompare(first);
  }
  return second ? -1 : first ? 1 : 0;
};

export const numberSortCompare = (
  order: SortDirection,
  first: number | undefined,
  second: number | undefined
): number => {
  if ((first || first === 0) && (second || second === 0)) {
    return (first - second) * (order === 'asc' ? 1 : -1);
  }
  if (order === 'asc') {
    return first || first === 0 ? -1 : second || second === 0 ? 1 : 0;
  }
  return first || first === 0 ? 1 : second || second === 0 ? -1 : 0;
};

export const entitySortCompare = <T extends Entity>(order: SortDirection) => {
  return (first: { data: T }, second: { data: T }): number => {
    return stringSortCompare(order, first.data?.name, second.data?.name);
  };
};

export const addressSortCompare = (order: SortDirection) => {
  return (first: { data: Address }, second: { data: Address }): number => {
    const firstAddress = getFullAddress(first.data);
    const secondAddress = getFullAddress(second.data);
    return stringSortCompare(
      order,
      firstAddress !== DEFAULT_EMPTY_VALUE ? firstAddress : undefined,
      secondAddress !== DEFAULT_EMPTY_VALUE ? secondAddress : undefined
    );
  };
};

export const telemetrySortCompare = (order: SortDirection) => {
  return (
    ...statuses:
      | { data: TelemetryStatus<string> }[]
      | { data: TelemetryStatus<number> }[]
  ): number => {
    if (statuses.length < 2) {
      // do not sort if not enough statuses to compare
      return 0;
    }

    const first = statuses[0].data?.value;
    const second = statuses[1].data?.value;

    const sameType = <T>(first: T, second: unknown): second is T => {
      return typeof second === typeof first;
    };

    if (first && second) {
      if (typeof first === 'string' && sameType(first, second)) {
        return stringSortCompare(order, first, second);
      }
      if (typeof first === 'number' && sameType(first, second)) {
        return numberSortCompare(order, first, second);
      }
      // do not sort if status values have different type
      return 0;
    }
    if (order === 'asc') {
      return first ? -1 : second ? 1 : 0;
    }
    return first ? 1 : second ? -1 : 0;
  };
};

export const mapperSortCompare = <T>(
  map: (value?: T) => number,
  order: SortDirection,
  first?: T,
  second?: T
): number => {
  return numberSortCompare(order, map(first), map(second));
};

export const processTableData = <T extends { summaryCounts?: SummaryCounts }>(
  entities: T[]
): (T & {
  [k in keyof SummaryCounts]: number;
})[] => {
  return entities.map((entity: T) => {
    return {
      ...flattenSummaryCounts(entity),
    };
  });
};

export const processAssetCountSummaryForFleets = ({
  rows,
  assetCountSummary,
}: {
  rows: Fleet[];
  assetCountSummary: AssetCountSummary;
}): (Fleet & { assetCount?: number })[] => {
  const { fleets } = assetCountSummary;
  if (fleets) {
    return rows.map((row: Fleet) => ({
      ...row,
      assetCount: fleets[row.id] ? fleets[row.id].numberOfAssets : 0,
    }));
  }
  return rows;
};

export const getFormattedPhone = (phone: BDTableData): string => {
  if (typeof phone === 'string') {
    const phoneMatch = phone
      .replace(/\D/g, '')
      .match(/^(\+1|1)?(\d{3})(\d{3})(\d{4})$/);

    if (phoneMatch) {
      const countryCode = '+1 ';

      return [
        countryCode,
        '(',
        phoneMatch[2],
        ') ',
        phoneMatch[3],
        '-',
        phoneMatch[4],
      ].join('');
    }
  }
  return DEFAULT_EMPTY_VALUE;
};

export const setDownloadState = (
  list: unknown[],
  filteredCount: number
): ToolbarButtonState => {
  return list && list.length > 0
    ? filteredCount === -1 || filteredCount > 0
      ? true
      : 'disabled'
    : 'disabled';
};
